// the hub page's two popups and the floating Filters pill.
//
//   HubCityButton     "Select a city" → a popup of city photo tiles, each a
//                     real link to that city's hub, with how many places the
//                     current category has there.
//   HubFloatingFilters  a navy pill pinned to the bottom of the screen; opens
//                     every filter in one pewter sheet (owner, concept "30 ·
//                     Filters in one sheet"): a price chart with a min / max
//                     control, type and sort as chips, and a live result count.
//
// Desktop: a centred pewter sheet. Phone (< 640px): a sheet from the bottom.
// Motion is opacity + transform only.
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import {
  MEAL_PLANS,
  NEAR_OPTIONS,
  STAY_AMENITIES,
  countStayFilters,
  withoutDatedFilters,
  type StayAmenity,
  type StayFilters,
} from "../../stays/lib/stay-filters";
import { RollingAmount } from "../../stays/components/rolling-amount";
import { createPortal } from "react-dom";
import { Link } from "@tanstack/react-router";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Icon } from "@majourneys/icons";
import { useCitiesOptional } from "../hooks/use-cities";
import { activitiesApi } from "../api/activities.api";
import type { ActivityFilters } from "../constants/query-keys";
import type { ShowFilter } from "../api/activities-query";
import { PW, pewterPanelApproved, pewterPrimary } from "@majourneys/booking";
import { SHEET_BOTTOM, SHEET_INSET, SHEET_OFFSCREEN } from "@majourneys/ui";
import {
  PriceHistogramBars,
  PriceRangeControl,
  priceBounds,
  priceScale,
  shownRange,
  type PriceHistogram,
  type StagedPrice,
} from "./hub-price-range";
import { pewterFrame, pewterInner, solid } from "../../../shared/ui/pewter-edge";
import { HUB_CONTROL, HUB_CONTROL_CLASS, setHubLoading } from "./hub-top";
import { BEVEL_CHIP_ON, BEVEL_INSET_NAVY, BEVEL_INSET_WHITE, BEVEL_NAVY_FACE, BEVEL_WHITE_FACE } from "@majourneys/shared/ui";

const NAVY = "#110E39";
const META = "#5a5d77";

const PANEL_RADIUS = 28;
// The approved pewter panel (a surface you read), lifted off the page.
const PANEL_BASE = pewterPanelApproved(PANEL_RADIUS);
const PANEL_FRAME = { ...PANEL_BASE, boxShadow: `${PANEL_BASE.boxShadow}, 0 30px 80px rgba(4,0,51,0.28)` };
const DROP_RADIUS = 24;
const DROP_FRAME = { ...pewterFrame(DROP_RADIUS, { gap: 4 }), boxShadow: "0 0 0 1px rgba(255,255,255,0.9), 0 24px 60px rgba(4,0,51,0.22)" };
const DROP_INNER = pewterInner(solid("#FAFAFC"), DROP_RADIUS, { gap: 4 });
const TILE_RADIUS = 18;
const WHERE_FRAME = {
  ...pewterFrame(999, { gap: 3 }),
  boxShadow: "0 0 0 1px rgba(255,255,255,0.9), 0 10px 26px -12px rgba(17,14,57,0.28)",
};
const WHERE_INNER = pewterInner(solid("#FFFFFF"), 999, { gap: 3 });
const TILE_FRAME = pewterFrame(TILE_RADIUS, { gap: 3 });

/* ---------------------------------------------------------------- dialog */

function useNarrow() {
  const [narrow, setNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return narrow;
}

function Dialog({
  open,
  onClose,
  title,
  width,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  width: number;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const { t } = useTranslation();
  const narrow = useNarrow();
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  // A phone's sheet follows a drag on its handle down and closes past 100px.
  const [dragY, setDragY] = useState(0);
  const drag = useRef<{ id: number; startY: number } | null>(null);
  useEffect(() => {
    if (!open) setDragY(0);
  }, [open]);
  const dragHandlers = narrow
    ? {
        onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
          if ((e.target as HTMLElement).closest("button")) return;
          drag.current = { id: e.pointerId, startY: e.clientY };
          e.currentTarget.setPointerCapture?.(e.pointerId);
        },
        onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
          if (drag.current?.id !== e.pointerId) return;
          setDragY(Math.max(0, e.clientY - drag.current.startY));
        },
        onPointerUp: (e: ReactPointerEvent<HTMLElement>) => {
          if (drag.current?.id !== e.pointerId) return;
          drag.current = null;
          if (dragY > 100) onClose();
          else setDragY(0);
        },
        onPointerCancel: () => {
          drag.current = null;
          setDragY(0);
        },
      }
    : {};

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    const id = window.setTimeout(() => setMounted(false), 260);
    return () => window.clearTimeout(id);
  }, [open]);

  // Focus goes into the panel on open, stays inside it on Tab, and goes
  // back to whatever opened it on close.
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const id = window.setTimeout(() => {
      const panel = panelRef.current;
      const first = panel?.querySelector<HTMLElement>("input, [data-autofocus]") ?? panel;
      first?.focus({ preventScroll: true });
    }, 60);
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !panelRef.current) return;
      const items = [...panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
      )].filter((el) => el.offsetParent !== null);
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onTab);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("keydown", onTab);
      opener?.focus?.({ preventScroll: true });
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!mounted || typeof document === "undefined") return null;

  const ease = "cubic-bezier(.2,.8,.2,1)";
  const panelStyle = narrow
    ? {
        ...PANEL_FRAME,
        // Floats 10px in from the sides and bottom, all corners round, so the
        // band shows whole (owner, 2026-10-11). Leaving clears the lift too.
        transform: shown ? `translateY(${dragY}px)` : SHEET_OFFSCREEN,
        // No transition while a finger holds it; the sheet settles or leaves on release.
        transition: drag.current ? "none" : `transform 300ms ${ease}`,
      }
    : {
        ...PANEL_FRAME,
        transform: shown ? "translateY(0) scale(1)" : "translateY(12px) scale(0.97)",
        opacity: shown ? 1 : 0,
        transition: `transform 260ms ${ease}, opacity 200ms ease`,
      };

  return createPortal(
    <div
      className={`fixed inset-0 z-[var(--z-modal)] flex ${narrow ? "items-end" : "items-center justify-center p-6"}`}
      style={narrow ? { paddingInline: SHEET_INSET, paddingBottom: SHEET_BOTTOM } : undefined}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
        style={{
          background: "rgba(17,14,57,0.42)",
          opacity: shown ? 1 : 0,
          transition: "opacity 220ms ease",
        }}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        className="relative w-full min-w-0 outline-none"
        style={{ maxWidth: narrow ? undefined : width, ...panelStyle }}
      >
        <div className="flex max-h-[min(86vh,760px)] flex-col">
          <div {...dragHandlers} className={narrow ? "touch-none" : undefined} data-testid="hub-sheet-grip">
          {narrow ? (
            <span aria-hidden="true" className="mx-auto mt-2 block h-1 w-10 rounded-full" style={{ background: PW.innerHairline }} />
          ) : null}
          <div className="flex items-center justify-between border-b px-5 pb-2.5 pt-3" style={{ borderColor: PW.hairline }}>
            <button
              type="button"
              onClick={onClose}
              aria-label={t("common.close", { defaultValue: "Close" })}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-[rgba(17,14,57,0.06)]"
              style={{ color: NAVY }}
            >
              <Icon name="ri-close-line" className="text-[20px]" aria-hidden="true" />
            </button>
            <h2 className="m-0 text-[16px] font-bold" style={{ color: NAVY }}>
              {title}
            </h2>
            <span className="w-9" />
          </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-5 pt-1.5">{children}</div>
          {footer ? (
            <div
              className="flex items-center justify-between gap-3 border-t px-5 pt-4"
              style={{ borderColor: PW.hairline, paddingBottom: 16 }}
            >
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------ city popup */

export interface CityRow {
  slug: string;
  name: string;
  image?: string;
  itemsCount?: number;
}

/**
 * How many places the current category has in each city. One request for the
 * whole category, grouped client-side, and only once the popup is opened.
 */
export function useCityCounts(filters: ActivityFilters, enabled: boolean) {
  return useQuery({
    queryKey: ["hub-city-counts", filters.category ?? "", filters.segment ?? "", filters.hub ?? "", filters.mode ?? "", filters.q ?? "", filters.lang ?? ""],
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { city: _c, page: _p, sort: _s, min_price: _a, max_price: _b, ...rest } = filters;
      // The API caps a page at 200 rows. Past that the counts would be short
      // and cities with places would vanish, so a capped result shows no
      // numbers and hides nothing.
      const res = (await activitiesApi.list({ ...rest, page_size: "200" })) as {
        data: { city?: { slug?: string } | null }[];
        total?: number;
      };
      const counts = new Map<string, number>();
      for (const row of res.data) {
        const slug = row.city?.slug;
        if (slug) counts.set(slug, (counts.get(slug) ?? 0) + 1);
      }
      const total = res.total ?? res.data.length;
      return { counts, total, complete: res.data.length >= total };
    },
  });
}

/** The phone's bottom bar plus its safe area: the open city list ends above it. */
const BOTTOM_CHROME = 110;
/** The open list's tallest size on a phone: its max-h class below, plus the gap. */
const listRoom = () => Math.min(480, window.innerHeight * 0.62, window.innerHeight - 280) + 8;

export function HubCityButton({
  lang,
  categorySlug,
  categoryName,
  citySlug,
  countFilters,
  source,
}: {
  lang: string;
  categorySlug?: string;
  categoryName?: string | null;
  citySlug?: string;
  /** The page's filters, for the per-city counts. */
  countFilters?: ActivityFilters;
  /**
   * A page with its own city list (the hotel-type pages, whose cities come
   * from the supplier and filter by `?city=`) passes the cities, their counts
   * and how to link to one. `slug` undefined is "All of Morocco".
   */
  source?: {
    cities: CityRow[];
    counts: Map<string, number>;
    total: number;
    link: (slug: string | undefined, props: { className: string; onClick: () => void; children: ReactNode }) => ReactNode;
  };
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [q, setQ] = useState("");
  // Non-suspending: the button draws at once and the city list fills in.
  // A page that passes its own cities never waits on this one.
  const { data } = useCitiesOptional();
  const cities = source?.cities ?? ((data?.data ?? []) as CityRow[]);
  // Not only when open: the hidden city links below need the counts too.
  const { data: countData } = useCityCounts(countFilters ?? {}, !!categorySlug && !source && !!countFilters);
  const counts = source
    ? { counts: source.counts, total: source.total, complete: true }
    : countData?.complete
      ? countData
      : undefined;

  const current = cities.find((c) => c.slug === citySlug);
  const linkableCities = countData?.complete
    ? cities.filter((c) => (countData.counts.get(c.slug) ?? 0) > 0)
    : [];
  // "1 place", "6 places". The category's own name is plural ("Activities"),
  // which reads wrong next to 1.
  const countLabel = (n: number) => t("explore.place_count", { count: n, defaultValue: n === 1 ? "1 place" : `${n} places` });

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return cities
      .map((c) => ({ ...c, n: counts ? counts.counts.get(c.slug) ?? 0 : c.itemsCount ?? 0 }))
      .filter((c) => (counts ? c.n > 0 || c.slug === citySlug : true))
      .filter((c) => !term || c.name.toLowerCase().includes(term))
      .sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
  }, [cities, counts, q, citySlug]);

  // Fade and lift away, then unmount.
  const closeTimer = useRef<number | undefined>(undefined);
  // A row was picked: dim the grid until the new city's cards arrive.
  const pick = (slug: string | undefined) => {
    if (slug !== citySlug) setHubLoading(true);
    close();
  };
  const close = () => {
    setShown(false);
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
      setQ("");
    }, 180);
  };

  // Picking a city lands on a new page with this picker still mounted: shut it
  // there, whatever the link's own click did.
  useEffect(() => {
    close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [citySlug]);

  // Fade in on the frame after mount; close on a tap outside or Escape.
  useEffect(() => {
    if (!open) {
      setShown(false);
      return;
    }
    const raf = requestAnimationFrame(() => setShown(true));
    // On a phone the floating Filters / Map pills and the bottom bar sat over
    // the open list (owner, 2026-11-03). The pills step aside while it is
    // open, and when the list would run under the bottom bar the page scrolls
    // the picker up so its rows end above it.
    const root = document.documentElement;
    root.setAttribute("data-city-picker-open", "");
    const fit = requestAnimationFrame(() => {
      const wrap = wrapRef.current;
      if (!wrap || window.matchMedia("(min-width: 1024px)").matches) return;
      const need = listRoom();
      const room = window.innerHeight - BOTTOM_CHROME - wrap.getBoundingClientRect().bottom;
      if (room < need) window.scrollBy({ top: need - room, behavior: "smooth" });
    });
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      cancelAnimationFrame(raf);
      cancelAnimationFrame(fit);
      root.removeAttribute("data-city-picker-open");
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <div ref={wrapRef} className="relative w-full sm:w-auto">
      {/* A full-width "Where to?" bar: the city's photo (or a pin for all
          of Morocco), the question, and the current choice under it. */}
      <button
        type="button"
        onClick={() => (open ? close() : (window.clearTimeout(closeTimer.current), setOpen(true)))}
        aria-haspopup="dialog"
        aria-expanded={open}
        // Pewter pill (owner, 2026-09-26, lab option A): the whole bar wears
        // the band, the same material as the Stays search pill.
        className="block w-full text-start transition-transform duration-200 active:scale-[0.99] sm:w-auto sm:min-w-[300px]"
        style={{ ...WHERE_FRAME, color: NAVY }}
      >
        <span className="flex h-14 items-center gap-3 py-1.5 pe-4 ps-1.5" style={WHERE_INNER}>
          <span
            className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-full"
            style={{
              background: "#EEF0F6",
              boxShadow: current?.image ? "0 0 0 2px #fff, 0 0 0 3px #D6DAEE" : undefined,
            }}
          >
            {current?.image ? (
              <img
                src={current.image.replace(/\/public$/, "/w=96,fit=scale-down,format=auto")}
                alt=""
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <Icon name="ri-map-pin-2-line" className="text-[20px]" aria-hidden="true" />
            )}
          </span>
          <span className="flex min-w-0 flex-1 flex-col leading-tight">
            <span className="text-[14px] font-semibold">{t("explore.where_to")}</span>
            <span className="break-words text-[13px]" style={{ color: META }}>
              {current?.name ?? t("explore.all_of_morocco")}
            </span>
          </span>
          <Icon
            name="ri-arrow-down-s-line"
            className={`text-[20px] transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            style={{ color: META }}
            aria-hidden="true"
          />
        </span>
      </button>

      {/* The city hubs for this category as plain links. The popup renders
          nothing until opened, and the old slider and dropdown carried these
          links; without them the category page stops linking its own city
          pages. Visually hidden, read as a normal list by screen readers.

          Only cities with something in this category: every city used to be
          listed, so each category page linked a dozen empty noindex hubs
          (Quad in Tetouan, Spas in Agafay), in all seven locales. Until the
          counts are in (or when they are capped) none are listed; the
          related links under the grid already name the stocked cities. */}
      {categorySlug && !source && linkableCities.length > 0 ? (
        <nav aria-label={t("explore.cities_nav", { defaultValue: "Cities" })} className="sr-only">
          <ul>
            {linkableCities.map((c) => (
              <li key={c.slug}>
                <Link to="/$lang/category/$category/$city" params={{ lang, category: categorySlug, city: c.slug }} tabIndex={-1}>
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      ) : null}

      {open ? (
        <div
          className={`absolute inset-x-0 top-full z-40 mt-2 origin-top transition-[opacity,transform] duration-200 ease-out sm:end-auto sm:w-[380px] ${
            shown ? "translate-y-0 scale-100 opacity-100" : "-translate-y-1 scale-[0.98] opacity-0"
          }`}
          style={DROP_FRAME}
          role="dialog"
          aria-label={t("explore.where_in_morocco", { defaultValue: "Where in Morocco?" })}
        >
          <div className="flex max-h-[min(62vh,480px)] flex-col overflow-hidden max-lg:max-h-[min(62vh,calc(100vh-280px),480px)]" style={DROP_INNER}>
            <div className="p-2 pb-1">
              <label className="flex h-10 items-center gap-2 rounded-full bg-white px-3.5" style={{ color: META, boxShadow: "0 0 0 1px rgba(17,14,57,0.1)" }}>
                <Icon name="ri-search-line" className="text-[16px]" aria-hidden="true" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={t("explore.search_city", { defaultValue: "Search a city" })}
                  aria-label={t("explore.search_city", { defaultValue: "Search a city" })}
                  className="min-w-0 flex-1 bg-transparent text-[16px] outline-none sm:text-[14px]"
                  style={{ color: NAVY }}
                />
              </label>
            </div>
            <ul className="m-0 list-none overflow-y-auto overscroll-contain p-1.5 pt-0">
              <li>
                <AllLink lang={lang} categorySlug={categorySlug} onClick={() => pick(undefined)} source={source}>
                  <DropRow on={!citySlug} name={t("explore.all_of_morocco", { defaultValue: "All of Morocco" })} sub={counts ? countLabel(counts.total) : undefined} />
                </AllLink>
              </li>
              {list.map((c) => {
                const row = <DropRow on={c.slug === citySlug} name={c.name} image={c.image} sub={counts ? countLabel(c.n) : undefined} />;
                return (
                  <li key={c.slug}>
                    {source ? (
                      source.link(c.slug, { className: "block no-underline", onClick: () => pick(c.slug), children: row })
                    ) : categorySlug ? (
                      <Link to="/$lang/category/$category/$city" params={{ lang, category: categorySlug, city: c.slug }} resetScroll={false} onClick={() => pick(c.slug)} className="block no-underline">
                        {row}
                      </Link>
                    ) : (
                      <Link to="/$lang/place/$city" params={{ lang, city: c.slug }} onClick={() => pick(c.slug)} className="block no-underline">
                        {row}
                      </Link>
                    )}
                  </li>
                );
              })}
              {list.length === 0 ? (
                <li className="py-6 text-center text-[14px]" style={{ color: META }}>
                  {t("explore.no_city_match", { defaultValue: "No city matches that." })}
                </li>
              ) : null}
            </ul>
          </div>
        </div>
      ) : null}
      </div>
    </>
  );
}

/** One row of the city dropdown: round photo (or globe), name, count, tick. */
function DropRow({ on, name, image, sub }: { on: boolean; name: string; image?: string | null; sub?: string }) {
  return (
    <span
      className="flex items-center gap-3 rounded-[16px] px-2 py-2 transition-colors hover:bg-white"
      style={on ? { background: "#fff", boxShadow: "0 0 0 1px rgba(17,14,57,0.1)" } : undefined}
    >
      <span className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full" style={{ background: "#EEF0F6", color: NAVY }}>
        {image ? (
          <img src={image.replace(/\/public$/, "/w=96,fit=scale-down,format=auto")} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <Icon name="ri-global-line" className="text-[20px]" aria-hidden="true" />
        )}
      </span>
      <span className="min-w-0 flex-1 leading-tight">
        <span className="block break-words text-[14.5px] font-semibold" style={{ color: NAVY }}>{name}</span>
        {sub ? <span className="block text-[12.5px]" style={{ color: META }}>{sub}</span> : null}
      </span>
      {on ? <Icon name="ri-check-line" className="text-[18px]" style={{ color: NAVY }} aria-hidden="true" /> : null}
    </span>
  );
}

function AllLink({
  lang,
  categorySlug,
  onClick,
  children,
  source,
}: {
  lang: string;
  categorySlug?: string;
  onClick: () => void;
  children: ReactNode;
  source?: { link: (slug: string | undefined, props: { className: string; onClick: () => void; children: ReactNode }) => ReactNode };
}) {
  if (source) return <>{source.link(undefined, { className: "block no-underline", onClick, children })}</>;
  return categorySlug ? (
    <Link to="/$lang/category/$category" params={{ lang, category: categorySlug }} resetScroll={false} onClick={onClick} className="block no-underline">
      {children}
    </Link>
  ) : (
    <Link to="/$lang/explore" params={{ lang }} onClick={onClick} className="block no-underline">
      {children}
    </Link>
  );
}

/* ------------------------------------------------------- floating filters */

/**
 * On a desk the Filters button sits in the control row beside the city picker
 * (owner, 2026-09-26, layout A) instead of floating over the grid. The two
 * buttons open the same popup, so the popup's open state and its "how many
 * filters are on" badge live here where both can reach them.
 */
const filtersStore = { open: false, count: 0, listeners: new Set<() => void>() };
function setFiltersStore(patch: Partial<{ open: boolean; count: number }>) {
  let changed = false;
  if (patch.open !== undefined && patch.open !== filtersStore.open) (filtersStore.open = patch.open), (changed = true);
  if (patch.count !== undefined && patch.count !== filtersStore.count) (filtersStore.count = patch.count), (changed = true);
  if (changed) filtersStore.listeners.forEach((l) => l());
}
function subscribeFilters(l: () => void) {
  filtersStore.listeners.add(l);
  return () => filtersStore.listeners.delete(l);
}
const useFiltersOpen = () => useSyncExternalStore(subscribeFilters, () => filtersStore.open, () => false);
const useFiltersCount = () => useSyncExternalStore(subscribeFilters, () => filtersStore.count, () => 0);
/** How many filters are on, for a summary elsewhere on the page (the stays bar). */
export const useHubFiltersCount = useFiltersCount;
/** Opens the Filters popup from anywhere on the page. */
export const openHubFilters = () => setFiltersStore({ open: true });

/** The small Filters pill for a pinned bar (the stays bar that follows the scroll). */
export function HubFiltersCompact() {
  const { t } = useTranslation();
  const count = useFiltersCount();
  return (
    <button
      type="button"
      onClick={() => setFiltersStore({ open: true })}
      aria-haspopup="dialog"
      className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full bg-white px-3.5 text-[13px] font-semibold transition-transform active:scale-[0.97]"
      style={{ color: NAVY, boxShadow: "0 0 0 1px rgba(17,14,57,0.12)" }}
    >
      <Icon name="ri-equalizer-line" className="text-[15px]" aria-hidden="true" />
      {t("filter.filters", { defaultValue: "Filters" })}
      {count > 0 ? (
        <span className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-bold text-white" style={{ background: NAVY }}>
          {count}
        </span>
      ) : null}
    </button>
  );
}

/** The desk-only Filters button for the control row. Hidden below lg, where the floating pill does the job. */
export function HubFiltersInline() {
  const { t } = useTranslation();
  const count = useFiltersCount();
  return (
    <button
      type="button"
      onClick={() => setFiltersStore({ open: true })}
      aria-haspopup="dialog"
      className="hidden h-14 shrink-0 items-center gap-2 rounded-full bg-white px-5 text-[15px] font-semibold transition-[transform,box-shadow] duration-200 hover:shadow-[0_1px_2px_rgba(17,14,57,0.06),0_10px_24px_-8px_rgba(17,14,57,0.24)] active:scale-[0.98] lg:inline-flex"
      style={{ color: NAVY, boxShadow: "0 0 0 1px rgba(17,14,57,0.08), 0 6px 18px -6px rgba(17,14,57,0.16)" }}
    >
      <Icon name="ri-equalizer-line" className="text-[18px]" aria-hidden="true" />
      {t("filter.filters", { defaultValue: "Filters" })}
      {count > 0 ? (
        <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[12px] font-bold text-white" style={{ background: NAVY }}>
          {count}
        </span>
      ) : null}
    </button>
  );
}

export interface HubFilterValue {
  min?: number;
  max?: number;
  sort: string;
  /** All (undefined) or Bookable, on the hubs that mix both. */
  show?: ShowFilter;
  /** Stars, pool, breakfast, free cancellation: the Stays hub only. */
  stay?: StayFilters;
  /** The chip groups' choices, by URL param (undefined = off). */
  chips?: Record<string, string | undefined>;
}

/**
 * A group of on/off chips in the Filters sheet (#54): "Open now" and "Tables
 * tonight", the restaurant collections. Each option sets one URL param to one
 * value; two options on the same param are one-of (the collections), options
 * on different params are independent (open, tonight).
 */
export interface FilterChipGroup {
  title: string;
  options: { param: string; value: string; label: string }[];
}

/** A pill you press: white with one navy hairline, the navy gradient when on. */
function chipStyle(on: boolean) {
  return on
    ? { ...pewterPrimary, boxShadow: BEVEL_CHIP_ON }
    : { background: BEVEL_WHITE_FACE, color: NAVY, boxShadow: `${BEVEL_INSET_WHITE}, 0 0 0 1px ${PW.controlEdge}` };
}

/** One group of single-choice chips, as a radio group. */
function ChipGroup<V extends string | undefined>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: V; label: string }[];
  value: V;
  onChange: (v: V) => void;
}) {
  return (
    <div className="mt-3 flex flex-wrap gap-2" role="radiogroup" aria-label={label}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value ?? "default"}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className="min-h-10 rounded-full px-4 text-[14px] font-semibold transition-[background,box-shadow,color] duration-200 active:scale-[0.98]"
            style={chipStyle(on)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function SheetSection({ title, first, children }: { title: string; first?: boolean; children: ReactNode }) {
  return (
    <section className={first ? "pt-3" : "mt-6 border-t pt-6"} style={{ borderColor: PW.hairline }}>
      <h3 className="m-0 text-[16px] font-bold" style={{ color: NAVY }}>
        {title}
      </h3>
      {children}
    </section>
  );
}

export function HubFloatingFilters({
  bounds,
  value,
  sortOptions,
  baseFilters,
  onApply,
  priceLabel,
  showPrice = true,
  staticCount,
  audience = false,
  floatingExtra,
  staySection,
  chipGroups,
  countFor,
}: {
  /** #54: the page's own filter groups as chips, counted live. */
  chipGroups?: FilterChipGroup[];
  /**
   * The list's filters for staged chip choices, for the live count. Built by
   * the page with the same helper as the list, so the number on the button is
   * the number the grid will show.
   */
  countFor?: (chips: Record<string, string | undefined>) => ActivityFilters;
  /**
   * The Stays hub's extra section. `withDates` unlocks breakfast and free
   * cancellation, which only a dated quote can answer.
   */
  staySection?: {
    withDates: boolean;
    /**
     * Stays left under the staged choices, price included, when the page
     * holds the list. Also drives the count beside each option.
     */
    count?: (staged: StayFilters, price: { min?: number; max?: number }) => number;
  };
  /** Another pill beside the phone's floating Filters (the Stays map toggle). */
  floatingExtra?: ReactNode;
  /** Show the All / Bookable switch: only on hubs whose grid holds both. */
  audience?: boolean;
  /** False on pages that only sort (the hotel-type pages). */
  showPrice?: boolean;
  /** The count for the button when there is no price to recount against. */
  staticCount?: number;
  /** "Price per person" for activities, "Price per night" for stays. */
  priceLabel: string;
  /** The span of the price control when the API sends no chart. */
  bounds: { min: number; max: number };
  value: HubFilterValue;
  sortOptions: { value: string; label: string }[];
  /** The page's filters without price/sort/page, for the live count and the chart. */
  baseFilters?: ActivityFilters;
  onApply: (next: HubFilterValue) => void;
}) {
  const { t } = useTranslation();
  const open = useFiltersOpen();
  const setOpen = (next: boolean) => setFiltersStore({ open: next });
  // A page change must not leave the popup open on the next page.
  useEffect(() => () => setFiltersStore({ open: false }), []);
  const [price, setPrice] = useState<StagedPrice>(() => ({ lo: value.min ?? 0, hi: value.max ?? null }));
  const [sort, setSort] = useState(value.sort);
  const [show, setShow] = useState<ShowFilter | undefined>(value.show);
  const [stay, setStay] = useState<StayFilters>(value.stay ?? {});
  const [chips, setChips] = useState<Record<string, string | undefined>>(value.chips ?? {});

  useEffect(() => {
    if (!open) return;
    setPrice({ lo: value.min ?? 0, hi: value.max ?? null });
    setSort(value.sort);
    setShow(value.show);
    setStay(value.stay ?? {});
    setChips(value.chips ?? {});
    // value.stay and value.chips are rebuilt from the URL each render; their params are the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value.min, value.max, value.sort, value.show, JSON.stringify(value.stay ?? {}), JSON.stringify(value.chips ?? {})]);

  const chipsOn = (c: Record<string, string | undefined> | undefined) =>
    Object.values(c ?? {}).filter((v) => v !== undefined && v !== "").length;
  const activeCount =
    (value.min != null || value.max != null ? 1 : 0) +
    (value.sort ? 1 : 0) +
    (audience && value.show ? 1 : 0) +
    (staySection ? countStayFilters(value.stay) : 0) +
    (chipGroups ? chipsOn(value.chips) : 0);
  useEffect(() => setFiltersStore({ count: activeCount }), [activeCount]);

  // The chart comes back with the count. The API draws it over every row the
  // other filters leave, before the price filter, so it keeps its shape while
  // the handles move; only the type (All / Bookable) reshapes it.
  const [histogram, setHistogram] = useState<PriceHistogram | null>(null);
  const scale = useMemo(() => priceScale(bounds, histogram), [bounds, histogram]);
  const staged = showPrice ? priceBounds(price, scale) : undefined;

  // Live count for the button, debounced by the query key settling.
  const stagedMin = staged?.min;
  const stagedMax = staged?.max;
  const chipsKey = JSON.stringify(chips);
  const [debounced, setDebounced] = useState<{ min?: number; max?: number; chips: Record<string, string | undefined> }>({
    min: stagedMin,
    max: stagedMax,
    chips,
  });
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced({ min: stagedMin, max: stagedMax, chips }), 250);
    return () => window.clearTimeout(id);
    // chips is keyed by its JSON.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stagedMin, stagedMax, chipsKey]);
  const withChips = !!chipGroups && !!countFor;
  const countBaseFilters = withChips ? countFor!(debounced.chips) : baseFilters;
  const { data: live } = useQuery({
    queryKey: ["hub-filter-count", countBaseFilters, debounced.min, debounced.max, audience ? show ?? "default" : "-", showPrice],
    enabled: open && (showPrice || audience || withChips) && !!countBaseFilters,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      // The staged All / Bookable replaces whatever the page applied. The
      // audience control only appears on BOOKABLE_FIRST_HUBS, where unset is
      // "Bookable" (with the API's fall-back) and "all" is the hub's union.
      const { mode: _mode, ...base } = countBaseFilters ?? {};
      const res = (await activitiesApi.list({
        ...(audience
          ? { ...base, ...(show === "all" ? {} : { mode: show ?? "bookable_first" }) }
          : countBaseFilters),
        page: "1",
        page_size: "1",
        ...(showPrice ? { histogram: "true" } : {}),
        ...(debounced.min != null && debounced.max != null
          ? { min_price: String(debounced.min), max_price: String(debounced.max) }
          : {}),
      })) as { total?: number; priceHistogram?: PriceHistogram | null };
      return { total: res.total ?? 0, histogram: res.priceHistogram ?? null };
    },
  });
  // Keep the last chart while the next one loads, so the bars never blink out.
  const liveHistogram = live?.histogram;
  useEffect(() => {
    if (liveHistogram !== undefined) setHistogram(liveHistogram);
  }, [liveHistogram]);

  const count = showPrice || audience || withChips ? live?.total : staticCount;
  const shown = shownRange(price, scale);

  return (
    <>
      <div
        // Clear of the phone's bottom tab bar (hidden from lg up).
        className="mj-hub-filters pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom,0px)+92px)] z-40 flex justify-center gap-2 lg:hidden"
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          className="pointer-events-auto inline-flex h-12 items-center gap-2 rounded-full px-5 text-[15px] font-semibold text-white transition-transform hover:scale-[1.03] active:scale-[0.97]"
          style={{ background: BEVEL_NAVY_FACE, boxShadow: `0 14px 30px -8px rgba(17,14,57,0.55), ${BEVEL_INSET_NAVY}` }}
        >
          <Icon name="ri-equalizer-line" className="text-[18px]" aria-hidden="true" />
          {t("filter.filters", { defaultValue: "Filters" })}
          {activeCount > 0 ? (
            <span className="ms-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1.5 text-[12px] font-bold" style={{ color: NAVY }}>
              {activeCount}
            </span>
          ) : null}
        </button>
        {floatingExtra}
      </div>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title={t("filter.filters", { defaultValue: "Filters" })}
        width={560}
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setPrice({ lo: 0, hi: null });
                setSort("");
                setShow(undefined);
                setStay({});
                // Filters only: the city, day, time and guests are not in here.
                setChips({});
              }}
              className="min-h-11 px-1 text-[15px] font-semibold underline underline-offset-4"
              style={{ color: NAVY }}
            >
              {t("filter.clear_all", { defaultValue: "Clear all" })}
            </button>
            <button
              type="button"
              onClick={() => {
                onApply({
                  min: staged?.min,
                  max: staged?.max,
                  sort,
                  show: audience ? show : value.show,
                  ...(chipGroups ? { chips } : {}),
                  ...(staySection
                    ? {
                        stay: staySection.withDates ? stay : withoutDatedFilters(stay),
                      }
                    : {}),
                });
                setOpen(false);
              }}
              className="h-12 rounded-full px-6 text-[15px] font-semibold transition active:scale-[0.98]"
              style={pewterPrimary}
            >
              {/* The live count asks the activities list only, which on the
                  Stays hub is the two camps: it said "Show 2 results" over a
                  hundred hotels. No number beats a wrong one there. */}
              {staySection?.count
                ? (
                    // The count rolls to its new value as choices change, so
                    // the guest sees each tap narrow the list.
                    <RollingAmount
                      text={t("filter.show_n_results", {
                        count: staySection.count(staySection.withDates ? stay : withoutDatedFilters(stay), staged ?? {}),
                      })}
                    />
                  )
                : withChips && count != null
                ? (
                    // Keyed on the number so each change pops in; the last
                    // number stays while the next one loads.
                    <span key={count} className="mj-pop-in inline-block tabular-nums" data-testid="hub-filter-show-count">
                      {t("filter.show_n_places", { count })}
                    </span>
                  )
                : !staySection && count != null
                ? t("filter.show_n_results", { count, defaultValue: `Show ${count} results` })
                : t("filter.show_results", { defaultValue: "Show results" })}
            </button>
          </>
        }
      >
        {chipGroups?.map((group, gi) => (
          <SheetSection key={group.title} title={group.title} first={gi === 0}>
            <div className="mt-3 flex flex-wrap gap-2" role="group" aria-label={group.title}>
              {group.options.map((o) => {
                const on = chips[o.param] === o.value;
                return (
                  <FilterChip
                    key={`${o.param}=${o.value}`}
                    on={on}
                    onClick={() => setChips((prev) => ({ ...prev, [o.param]: on ? undefined : o.value }))}
                  >
                    {o.label}
                  </FilterChip>
                );
              })}
            </div>
          </SheetSection>
        ))}

        {showPrice ? (
          <SheetSection title={priceLabel} first={!chipGroups?.length}>
            <div className="mt-4">
              {histogram ? <PriceHistogramBars histogram={histogram} range={shown} scale={scale} /> : null}
              <PriceRangeControl scale={scale} value={price} onChange={setPrice} />
            </div>
          </SheetSection>
        ) : null}

        {staySection ? (
          <StayFilterSection
            value={stay}
            onChange={setStay}
            withDates={staySection.withDates}
            countWith={
              staySection.count
                ? (next) => staySection.count!(staySection.withDates ? next : withoutDatedFilters(next), staged ?? {})
                : undefined
            }
          />
        ) : null}

        {audience ? (
          <SheetSection title={t("filter.show_group", { defaultValue: "Filter by type" })} first={!showPrice && !staySection && !chipGroups?.length}>
            <ChipGroup<ShowFilter | undefined>
              label={t("filter.show_group", { defaultValue: "Filter by type" })}
              options={[
                // Unset is the default, and the default is bookable-only.
                { value: undefined, label: t("filter.show_bookable", { defaultValue: "Bookable" }) },
                { value: "all", label: t("filter.show_all", { defaultValue: "All" }) },
              ]}
              value={show}
              onChange={setShow}
            />
          </SheetSection>
        ) : null}

        <SheetSection title={t("sort.label", { defaultValue: "Sort by" })} first={!showPrice && !audience && !staySection && !chipGroups?.length}>
          <ChipGroup
            label={t("sort.label", { defaultValue: "Sort by" })}
            options={sortOptions}
            value={sort}
            onChange={setSort}
          />
        </SheetSection>
      </Dialog>
    </>
  );
}

/** A pill that is on or off, in the panel's own look. */
function FilterChip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className="inline-flex min-h-10 items-center gap-1.5 rounded-full px-4 text-[13px] transition-[background,box-shadow,color] duration-200"
      style={{
        color: on ? "#fff" : NAVY,
        fontWeight: on ? 700 : 500,
        background: on ? BEVEL_NAVY_FACE : BEVEL_WHITE_FACE,
        boxShadow: on ? BEVEL_CHIP_ON : `${BEVEL_INSET_WHITE}, 0 0 0 1px rgba(17,14,57,0.14)`,
      }}
    >
      {children}
    </button>
  );
}

/**
 * One folding section of the Stays filters. Closed, the header carries a
 * short summary of what is chosen, so a folded panel still reads as a list of
 * the guest's choices.
 */
function FoldSection({
  title,
  summary,
  defaultOpen,
  children,
}: {
  title: string;
  summary?: string | null;
  defaultOpen: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-t" style={{ borderColor: "rgba(17,14,57,0.08)" }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-14 w-full items-center gap-2 py-4 text-start"
      >
        <span className="text-[16px] font-bold" style={{ color: NAVY }}>{title}</span>
        {summary ? (
          <span className="min-w-0 break-words text-[13px] font-medium" style={{ color: META }}>{summary}</span>
        ) : null}
        <Icon
          name="ri-arrow-down-s-line"
          className="ms-auto shrink-0 text-[20px]"
          style={{ color: NAVY, transform: open ? "rotate(180deg)" : "none", transition: "transform 200ms ease" }}
          aria-hidden="true"
        />
      </button>
      {open ? <div className="pb-6">{children}</div> : null}
    </section>
  );
}

/**
 * The Stays sections: stars, amenities, distance, then the two a dated quote
 * answers. Inside the pewter panel the choices are plain controls, white with
 * one hairline and navy when on: the band belongs to the surface you read,
 * never to a control inside it.
 */
function StayFilterSection({
  value,
  onChange,
  withDates,
  countWith,
}: {
  value: StayFilters;
  onChange: (next: StayFilters) => void;
  withDates: boolean;
  countWith?: (next: StayFilters) => number;
}) {
  const { t } = useTranslation();
  const amenities = value.amenities ?? [];
  const toggleAmenity = (a: StayAmenity) =>
    onChange({ ...value, amenities: amenities.includes(a) ? amenities.filter((x) => x !== a) : [...amenities, a] });
  const needsDates = t("stays.hub_filters.needs_dates");
  const mealLabel = (m: (typeof MEAL_PLANS)[number]) => t(`stays.hub_filters.meal_${m}`);

  return (
    <div className="mt-7">
      <FoldSection
        title={t("stays.hub_filters.stars")}
        summary={value.stars ? (value.stars === 5 ? "5" : `${value.stars}+`) : null}
        defaultOpen
      >
        <div className="flex flex-wrap gap-2">
          {([undefined, 3, 4, 5] as const).map((n) => (
            <FilterChip key={n ?? "any"} on={value.stars === n} onClick={() => onChange({ ...value, stars: n })}>
              {n ? (
                <>
                  {n === 5 ? "5" : `${n}+`}
                  <Icon name="ri-star-fill" className="text-[12px] text-[#E5B265]" aria-hidden="true" />
                </>
              ) : (
                t("stays.hub_filters.any")
              )}
            </FilterChip>
          ))}
        </div>
      </FoldSection>

      <FoldSection
        title={t("stays.hub_filters.amenities_title")}
        summary={amenities.length ? t("stays.hub_filters.selected", { count: amenities.length }) : null}
        defaultOpen
      >
        <div className="flex flex-wrap gap-2">
          {STAY_AMENITIES.map((a) => {
            const on = amenities.includes(a);
            // What the list would hold with this one added; hidden at zero
            // unless it is already on, so no chip leads to an empty page.
            const n = countWith ? countWith({ ...value, amenities: on ? amenities : [...amenities, a] }) : null;
            if (!on && n === 0) return null;
            return (
              <FilterChip key={a} on={on} onClick={() => toggleAmenity(a)}>
                {t(`stays.hub_filters.amenity.${a}`)}
                {n != null ? <span style={{ opacity: 0.6 }}>{n}</span> : null}
              </FilterChip>
            );
          })}
        </div>
      </FoldSection>

      <FoldSection
        title={t("stays.hub_filters.distance_title")}
        summary={value.near ? t(`stays.hub_filters.near_${value.near}`) : null}
        defaultOpen={!!value.near}
      >
        <div className="flex flex-wrap gap-2">
          <FilterChip on={!value.near} onClick={() => onChange({ ...value, near: undefined })}>
            {t("stays.hub_filters.any")}
          </FilterChip>
          {NEAR_OPTIONS.map((m) => (
            <FilterChip key={m} on={value.near === m} onClick={() => onChange({ ...value, near: m })}>
              {t(`stays.hub_filters.near_${m}`)}
            </FilterChip>
          ))}
        </div>
        <p className="m-0 mt-3 text-[13px]" style={{ color: META }}>
          {t("stays.hub_filters.distance_note")}
        </p>
      </FoldSection>

      <FoldSection
        title={t("stays.hub_filters.meal_title")}
        summary={!withDates ? needsDates : value.meal ? mealLabel(value.meal) : null}
        defaultOpen={withDates && !!value.meal}
      >
        {withDates ? (
          <div className="flex flex-wrap gap-2">
            <FilterChip on={!value.meal} onClick={() => onChange({ ...value, meal: undefined })}>
              {t("stays.hub_filters.any")}
            </FilterChip>
            {MEAL_PLANS.map((m) => (
              <FilterChip key={m} on={value.meal === m} onClick={() => onChange({ ...value, meal: m })}>
                {mealLabel(m)}
              </FilterChip>
            ))}
          </div>
        ) : (
          <p className="m-0 text-[13px]" style={{ color: META }}>{t("stays.hub_filters.dates_hint")}</p>
        )}
      </FoldSection>

      <FoldSection
        title={t("stays.hub_filters.cancel_title")}
        summary={!withDates ? needsDates : value.freeCancel ? t("stays.hub_filters.free_cancel") : null}
        defaultOpen={withDates && !!value.freeCancel}
      >
        {withDates ? (
          <FilterChip
            on={!!value.freeCancel}
            onClick={() => onChange({ ...value, freeCancel: value.freeCancel ? undefined : true })}
          >
            {t("stays.hub_filters.free_cancel")}
          </FilterChip>
        ) : (
          <p className="m-0 text-[13px]" style={{ color: META }}>{t("stays.hub_filters.dates_hint")}</p>
        )}
      </FoldSection>
    </div>
  );
}
